// Client-side session store. Holds the short-lived access token + rotating refresh token the API
// issued after login (no secret is ever computed here — the browser only carries what the API gave
// it). Persisted in localStorage so a reload keeps the session; cleared on logout / refresh failure.
//
// In dev the API's auth stub answers without a token, so the app works with an EMPTY store — real
// login is exercised via /login but not forced. In production (stub off) the API 401s and the api
// client redirects to /login.

import type { AuthUser } from '@crosscastr/shared';

const ACCESS_KEY = 'cc.accessToken';
const REFRESH_KEY = 'cc.refreshToken';
const USER_KEY = 'cc.user';

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}
export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function setSession(accessToken: string, refreshToken: string, user?: AuthUser): void {
  localStorage.setItem(ACCESS_KEY, accessToken);
  localStorage.setItem(REFRESH_KEY, refreshToken);
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/** Update just the tokens (refresh rotation) without touching the cached user. */
export function setTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_KEY, accessToken);
  localStorage.setItem(REFRESH_KEY, refreshToken);
}

export function clearSession(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}
